import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/system';
import { QRCodeCanvas } from 'qrcode.react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { BACKEND_URL } from '../../api/backend';
import DashNav from '../Dashboard/DashNav';

const PopupContainer = styled(Box)(() => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  // Same footprint as the account dash console so switching between the
  // two doesn't change the card size
  width: '90%',
  maxWidth: '400px',
  boxSizing: 'border-box',
  padding: '20px',
  background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
  border: '2px solid #00BFFF',
  borderRadius: '16px',
  textAlign: 'center',
  // Above the Dashboard overlay (z-index 9999) so Buy Spins shows over it.
  zIndex: 10001,
  boxShadow: '0 0 15px rgba(0, 191, 255, 0.3)',
  // 86vh guards against mobile vh including collapsed-toolbar space
  // (90vh overflowed the visible area and clipped the title); dvh below
  // tracks the real visible viewport where supported
  maxHeight: '86vh',
  '@supports (height: 1dvh)': {
    maxHeight: '92dvh',
  },
  overflowY: 'auto',
  // Site-wide scroller style: hidden scrollbar, scrolling preserved
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '@media (max-width: 600px)': {
    padding: '15px'
  }
}));

const Overlay = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'rgba(8, 11, 19, 0.65)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  zIndex: 10000
}));

const RetroTitle = styled(Typography)(() => ({
  color: '#FFFFFF',
  fontSize: '1rem',
  textShadow: '0 0 10px #FFFFFF, 0 0 20px #00BFFF, 0 0 30px #00BFFF',
  fontFamily: "'Press Start 2P', cursive",
  marginBottom: '14px',
  marginTop: '8px',
  '@media (max-width: 600px)': {
    fontSize: '0.78rem'
  }
}));

const SummaryText = styled(Typography)(() => ({
  color: '#FFFFFF',
  fontSize: '0.95rem',
  marginBottom: '15px',
  '@media (max-width: 600px)': {
    fontSize: '0.85rem'
  }
}));

const CopyField = styled(TextField)(() => ({
  backgroundColor: '#283593',
  borderRadius: '5px',
  marginBottom: '15px',
  input: {
    color: '#FFFFFF',
    fontFamily: "'Press Start 2P', cursive",
    fontSize: '0.9rem',
    '@media (max-width: 600px)': {
      fontSize: '0.8rem'
    }
  }
}));

const PlanGrid = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
  width: '100%',
  justifyContent: 'space-between',
  marginBottom: '18px'
}));

const PLAN_OPTIONS = [
  {
    price: 0,
    description: 'Sign up & unlock 3 more free games',
    games: 3,
    label: 'Free Signup'
  },
  {
    price: 5,
    description: 'Buy 10 spins (1 game)',
    games: 10,
    label: '$5 Pack'
  },
  {
    price: 10,
    description: 'Buy 50 spins (5 games)',
    games: 50,
    label: '$10 Pack'
  },
  {
    price: 25,
    description: 'Buy 150 spins (15 games)',
    games: 150,
    label: '$25 Pack'
  }
];

const SubscriptionOption = ({ plan, selectedPrice, onSelect, disabled }) => {
  const isSelected = selectedPrice === plan.price;

  return (
    <Box
      onClick={() => {
        if (!disabled) {
          onSelect(plan.price);
        }
      }}
      sx={{
        backgroundColor: disabled
          ? 'rgba(255, 255, 255, 0.08)'
          : isSelected
            ? 'rgba(0, 191, 255, 0.3)'
            : 'rgba(0, 191, 255, 0.1)',
        padding: '15px',
        borderRadius: '8px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: isSelected
          ? '2px solid #00BFFF'
          : '1px solid rgba(255, 255, 255, 0.2)',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: disabled
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(0, 191, 255, 0.2)',
          transform: disabled ? 'none' : 'translateY(-2px)'
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '1 1 calc(50% - 12px)',
        minWidth: '140px',
        boxSizing: 'border-box',
        opacity: disabled ? 0.6 : 1,
        '@media (max-width: 600px)': { padding: '10px' }
      }}
    >
      <Typography
        sx={{
          color: '#FFFFFF',
          fontFamily: "'Press Start 2P', cursive",
          fontSize: '0.85rem',
          mb: 0.75,
          '@media (max-width: 600px)': { fontSize: '0.72rem' }
        }}
      >
        {plan.label}
      </Typography>
      <Typography
        sx={{
          color: '#FFFFFF',
          fontSize: '0.75rem',
          textAlign: 'center',
          '@media (max-width: 600px)': { fontSize: '0.7rem' }
        }}
      >
        {plan.description}
      </Typography>
      <Typography sx={{ color: '#00E5FF', fontSize: '0.65rem', mt: 0.75 }}>
        +{plan.games} spins
      </Typography>
    </Box>
  );
};

// Only paid packs are purchasable here — free spins are granted on account signup.
const PAID_PLANS = PLAN_OPTIONS.filter((plan) => plan.price > 0);

const PaymentPopup = ({ onClose, initialPlan }) => {
  const {
    isAuthenticated,
    account,
    spinsRemaining,
    purchaseSpins,
    openAuth
  } = useAuth();
  const playerEmail = account?.email || '';
  const [selectedPrice, setSelectedPrice] = useState(() => {
    const initial = PAID_PLANS.find((plan) => plan.price === initialPlan);
    return initial ? initial.price : PAID_PLANS[0].price;
  });
  const [cryptoAmount, setCryptoAmount] = useState('0.00015');
  const [usdAmount, setUsdAmount] = useState(() => `$${selectedPrice}.00`);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [email, setEmail] = useState(() => playerEmail);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedPlan = useMemo(
    () => PAID_PLANS.find((plan) => plan.price === selectedPrice) || PAID_PLANS[0],
    [selectedPrice]
  );

  useEffect(() => {
    if (playerEmail) {
      setEmail(playerEmail);
    }
  }, [playerEmail]);

  useEffect(() => {
    if (selectedPlan.price === 0) {
      setUsdAmount('$0.00');
      setCryptoAmount('0');
      setLoadingPrices(false);
      return;
    }

    let isMounted = true;
    const fetchCryptoPrices = async () => {
      setLoadingPrices(true);
      try {
        // Live prices from the shared backend (CoinMarketCap). The relative
        // URL previously hit this app's own server, which served a hardcoded
        // stub (BTC 65000) — buyers were quoted stale conversion amounts.
        const response = await axios.get(`${BACKEND_URL}/api/crypto-payment-prices`);
        const prices = response.data.prices;

        const cryptoEquivalents = {
          BTC: (selectedPlan.price / prices.BTC).toFixed(8),
          ETH: (selectedPlan.price / prices.ETH).toFixed(8),
          DOGE: (selectedPlan.price / prices.DOGE).toFixed(2),
          ZEC: (selectedPlan.price / prices.ZEC).toFixed(4)
        };

        if (isMounted) {
          setCryptoAmount(cryptoEquivalents[selectedCrypto]);
          setUsdAmount(`$${selectedPlan.price}.00`);
        }
      } catch (error) {
        console.error('Error fetching crypto prices:', error);
        if (isMounted) {
          setCryptoAmount('N/A');
        }
      } finally {
        if (isMounted) {
          setLoadingPrices(false);
        }
      }
    };

    fetchCryptoPrices();

    return () => {
      isMounted = false;
    };
  }, [selectedPlan.price, selectedCrypto]);

  useEffect(() => {
    if (selectedPlan.price === 0) {
      setConfirmChecked(false);
    }
  }, [selectedPlan.price]);

  const cryptoAddresses = {
    BTC: '3KybzUMnSGkJMNmoshXD7wwaeLuENQNpNB',
    ETH: '0xB8ea0cEF886Fa141B3c60E220320FA911d3bBC99',
    DOGE: 'DGRAizMZUuQuqMLSTUt7AxoEsktYryA6s1',
    ZEC: 't1WZrmSP3ZjxxpZqX5zCDpXX6P1UKtgvNHE'
  };

  const handlePlanSelect = (price) => {
    setSelectedPrice(price);
  };

  const handleCryptoSelect = (crypto) => {
    setSelectedCrypto(crypto);
  };

  const handleCryptoAmountChange = (event) => {
    setCryptoAmount(event.target.value);
  };

  const handleIncrement = () => {
    setCryptoAmount((prevAmount) => {
      const numeric = Number(prevAmount);
      if (!Number.isFinite(numeric)) {
        return prevAmount;
      }
      const nextAmount = numeric + 0.00001;
      return nextAmount.toFixed(8);
    });
  };

  const handleDecrement = () => {
    setCryptoAmount((prevAmount) => {
      const numeric = Number(prevAmount);
      if (!Number.isFinite(numeric)) {
        return prevAmount;
      }
      const nextAmount = Math.max(numeric - 0.00001, 0);
      return nextAmount.toFixed(8);
    });
  };

  const handleConfirmChange = (event) => {
    setConfirmChecked(event.target.checked);
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(cryptoAddresses[selectedCrypto]);
    alert('Crypto Address Copied!');
  };

  const handlePaidSubmit = async () => {
    if (!isAuthenticated) {
      openAuth('purchase');
      onClose();
      return;
    }

    if (!confirmChecked) {
      alert('Please confirm the payment before proceeding.');
      return;
    }

    setSubmitting(true);
    try {
      // Trust-based purchase against the shared backend: credits spins,
      // records the purchase in Mongo, and emails the user + admin. The new
      // balance is reflected in the UI immediately via AuthContext.
      const result = await purchaseSpins({
        packId: selectedPlan.label,
        spins: selectedPlan.games,
        usdAmount: selectedPlan.price,
        cryptoAmount,
        selectedCrypto
      });
      alert(`Purchase complete! ${selectedPlan.games} spins added. New balance: ${result.spinsRemaining} spins.`);
      onClose();
    } catch (error) {
      console.error('Error processing payment:', error);
      alert(error.message || 'Failed to process payment. Please try again.');
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    handlePaidSubmit();
  };

  // Purchases require an account so the balance persists in Mongo.
  if (!isAuthenticated) {
    return (
      <>
        <Overlay onClick={onClose} />
        <PopupContainer>
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', top: 10, right: 10, color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
          <RetroTitle>Log In to Buy Spins</RetroTitle>
          <SummaryText>
            Create a free account (or log in) to buy spins. Your balance and
            purchase history are saved to your account.
          </SummaryText>
          <Button
            onClick={() => {
              openAuth('purchase');
              onClose();
            }}
            sx={{
              backgroundColor: '#00BFFF',
              color: '#FFFFFF',
              fontFamily: "'Press Start 2P', cursive",
              textTransform: 'none',
              '&:hover': { backgroundColor: '#00A0E0' },
              marginTop: '10px',
              padding: '12px',
              fontSize: { xs: '0.8rem', sm: '0.95rem' }
            }}
            fullWidth
          >
            Log In / Sign Up
          </Button>
        </PopupContainer>
      </>
    );
  }

  return (
    <>
      <Overlay onClick={onClose} />
      <PopupContainer>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: 'white'
          }}
        >
          <CloseIcon />
        </IconButton>

        <RetroTitle>Unlock More Spins</RetroTitle>

        <Typography sx={{ color: '#9dd5ff', fontSize: '0.75rem', mb: 2 }}>
          Current balance: {spinsRemaining.toLocaleString()} spins
        </Typography>

        <PlanGrid>
          {PAID_PLANS.map((plan) => (
            <SubscriptionOption
              key={plan.price}
              plan={plan}
              selectedPrice={selectedPrice}
              onSelect={handlePlanSelect}
              disabled={false}
            />
          ))}
        </PlanGrid>

        {(
          <>
            <QRCodeCanvas
              value={cryptoAddresses[selectedCrypto]}
              size={130}
              style={{ margin: '8px auto', maxWidth: '100%', height: 'auto' }}
            />

            <CopyField
              value={cryptoAddresses[selectedCrypto]}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <ContentCopyIcon
                      onClick={handleCopyToClipboard}
                      sx={{ cursor: 'pointer', color: '#FFFFFF' }}
                    />
                  </InputAdornment>
                )
              }}
              fullWidth
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Bitcoin">
                  <Typography
                    onClick={() => handleCryptoSelect('BTC')}
                    sx={{
                      color: selectedCrypto === 'BTC' ? '#FFFFFF' : '#00BFFF',
                      cursor: 'pointer',
                      fontWeight: selectedCrypto === 'BTC' ? 'bold' : 'normal'
                    }}
                  >
                    BTC
                  </Typography>
                </Tooltip>
                <Typography sx={{ color: '#FFFFFF' }}>|</Typography>
                <Tooltip title="Ethereum">
                  <Typography
                    onClick={() => handleCryptoSelect('ETH')}
                    sx={{
                      color: selectedCrypto === 'ETH' ? '#FFFFFF' : '#00BFFF',
                      cursor: 'pointer',
                      fontWeight: selectedCrypto === 'ETH' ? 'bold' : 'normal'
                    }}
                  >
                    ETH
                  </Typography>
                </Tooltip>
                <Typography sx={{ color: '#FFFFFF' }}>|</Typography>
                <Tooltip title="Dogecoin">
                  <Typography
                    onClick={() => handleCryptoSelect('DOGE')}
                    sx={{
                      color: selectedCrypto === 'DOGE' ? '#FFFFFF' : '#00BFFF',
                      cursor: 'pointer',
                      fontWeight: selectedCrypto === 'DOGE' ? 'bold' : 'normal'
                    }}
                  >
                    DOGE
                  </Typography>
                </Tooltip>
                <Typography sx={{ color: '#FFFFFF' }}>|</Typography>
                <Tooltip title="Zcash">
                  <Typography
                    onClick={() => handleCryptoSelect('ZEC')}
                    sx={{
                      color: selectedCrypto === 'ZEC' ? '#FFFFFF' : '#00BFFF',
                      cursor: 'pointer',
                      fontWeight: selectedCrypto === 'ZEC' ? 'bold' : 'normal'
                    }}
                  >
                    ZEC
                  </Typography>
                </Tooltip>
              </Box>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
              sx={{
                gap: '8px',
                '@media (max-width: 600px)': {
                  flexDirection: 'row',
                  alignItems: 'center'
                }
              }}
            >
              <IconButton
                onClick={handleDecrement}
                sx={{
                  color: '#FFFFFF',
                  backgroundColor: '#283593',
                  borderRadius: '5px',
                  padding: '8px'
                }}
              >
                <RemoveIcon />
              </IconButton>
              <CopyField
                value={cryptoAmount}
                onChange={handleCryptoAmountChange}
                fullWidth
                sx={{ textAlign: 'center' }}
              />
              <IconButton
                onClick={handleIncrement}
                sx={{
                  color: '#FFFFFF',
                  backgroundColor: '#283593',
                  borderRadius: '5px',
                  padding: '8px'
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            <Box
              sx={{
                backgroundColor: 'rgba(0, 191, 255, 0.1)',
                borderRadius: '8px',
                padding: '15px',
                mb: 2
              }}
            >
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  mb: 1
                }}
              >
                {loadingPrices ? 'Loading prices…' : `${cryptoAmount} ${selectedCrypto}`}
              </Typography>
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontSize: '0.85rem',
                  textAlign: 'center'
                }}
              >
                Unlock {selectedPlan.games} spins for {usdAmount}
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmChecked}
                  onChange={handleConfirmChange}
                  sx={{
                    color: '#00BFFF',
                    '&.Mui-checked': {
                      color: '#00BFFF'
                    }
                  }}
                />
              }
              label="I confirm this payment"
              sx={{
                color: '#FFFFFF',
                fontSize: '0.9rem',
                alignSelf: 'center',
                mb: 2
              }}
            />

            <TextField
              label="Email for Receipt"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                readOnly: Boolean(playerEmail)
              }}
              fullWidth
              sx={{
                backgroundColor: '#283593',
                borderRadius: '5px',
                mb: 2,
                input: {
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '0.8rem'
                },
                label: {
                  color: '#FFFFFF'
                }
              }}
            />

            <SummaryText>
              You confirm this 1-time payment (no auto-pay), adding {selectedPlan.games}{' '}
              spins to your account ({usdAmount}).
            </SummaryText>

            <Button
              onClick={handleSubmit}
              disabled={!confirmChecked || submitting}
              sx={{
                backgroundColor: confirmChecked && !submitting ? '#00BFFF' : 'rgba(0, 191, 255, 0.3)',
                color: '#FFFFFF',
                fontFamily: "'Press Start 2P', cursive",
                fontSize: '0.78rem',
                padding: '10px 20px',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: confirmChecked && !submitting ? '#00A0E0' : 'rgba(0, 191, 255, 0.3)'
                },
                textTransform: 'none',
                width: '100%'
              }}
            >
              {submitting ? 'Processing…' : 'Complete & Send Receipt'}
            </Button>
          </>
        )}

        {/* Same plain word links as the account dash bottom nav */}
        <DashNav current="Buy Spins" onNavigate={onClose} />
      </PopupContainer>
    </>
  );
};

export default PaymentPopup;
